import { useEffect, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchPublicForm } from "../api/queries";
import { fetchPublicCustomer } from "../api/queries";
import { QRCodeSVG } from "qrcode.react";
import { ShareIcon, LinkIcon } from "@heroicons/react/24/outline";
import LanguageSelector from "../components/LanguageSelector";
export default function PublicForm() {
  const { t } = useLanguage();
  const { formId, customerId } = useParams();

  const { data: customer, isLoading: customerLoading } = useQuery({
    queryKey: ["customer", customerId],
    queryFn: () => fetchPublicCustomer(customerId),
  });

  const { data: form } = useQuery({
    queryKey: ["publicForm", formId, customer?.referral_form_uuid],
    queryFn: async () => {
      try {
        return await fetchPublicForm(formId);
      } catch (err) {
        // The form uuid baked into the wallet /r/ link goes stale whenever the
        // pass's form is re-created (e.g. a card-type change deletes and
        // rebuilds pass + form), which used to leave this page stuck on
        // "Loading…". Fall back to the customer's current form so the referral
        // page (and the share links below) self-heal.
        const fallback = customer?.referral_form_uuid;
        if (fallback && fallback !== formId) {
          return await fetchPublicForm(fallback);
        }
        throw err;
      }
    },
    // Wait for the customer query to settle so the fallback uuid is available
    // on the first form fetch attempt.
    enabled: !customerLoading,
  });

  // The form that actually resolved — use this (not the URL param) for the
  // share/QR links so a healed stale link points people at the live form.
  const shareFormId = form?.uuid ?? formId;

  const [copied, setCopied] = useState(false);

  // Add effect to monitor data changes
  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied]);

  return (
    <>
      {customer && form ? (
        <>
          <div className="absolute top-4 right-4">
            <LanguageSelector />
          </div>
          <div
            className="flex flex-col justify-center items-center h-screen text-center mx-auto md:pt-32"
            style={{
              lineHeight: "1.5",
              backgroundColor: form.background_color,
              color: form.text_color,
            }}
          >
            <div className="w-full flex items-center justify-center gap-2 pt-8">
              <div className="flex flex-col items-center gap-2">
                <img
                  src={form.extras.store_logo_url}
                  alt="Store Logo"
                  className="w-16 h-auto md:w-24"
                />
                <p className="text-xl font-medium md:text-2xl">
                  {form.extras.store_name}
                </p>
              </div>
            </div>

            <div className="mt-8 mx-6 md:text-xl">
              <h1>{t("public.referral.hi", { name: customer.name })}</h1>
              {form.extras.reward_enabled ? (
                <p>{t("public.referral.share.hasReward")}</p>
              ) : (
                <p>{t("public.referral.share.noReward")}</p>
              )}
            </div>

            <div className="w-full mt-8">
              <div className="border-2 rounded-2xl max-w-xs bg-white mx-auto p-4">
                <QRCodeSVG
                  style={{ width: "100%", height: "100%" }}
                  value={`${window.location.origin}/f/${shareFormId}?r=${customerId}`}
                  level="H"
                  marginSize={2}
                />
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={() => {
                  navigator
                    .share({
                      title: t("public.referral.shareMessage.title"),
                      url: `${window.location.origin}/f/${shareFormId}?r=${customerId}`,
                      text: t("public.referral.shareMessage.text"),
                    })
                    .catch((error) => {
                      // Silently handle abort errors when user cancels share dialog
                      if (error.name !== "AbortError") {
                        console.error("Error sharing:", error);
                      }
                    });
                }}
                className="flex w-32 items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg transition-colors duration-200 shadow-md"
              >
                <ShareIcon className="w-5 h-5" />
                <span>{t("public.referral.shareButton")}</span>
              </button>

              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/f/${shareFormId}?r=${customerId}`,
                  );
                  setCopied(true);
                }}
                className="flex w-32 items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors duration-200 shadow-md"
              >
                <LinkIcon className="w-5 h-5" />
                {copied ? (
                  <span>{t("public.referral.copied")}</span>
                ) : (
                  <span>{t("public.referral.copyButton")}</span>
                )}
              </button>
            </div>
            <div className="flex items-center gap-2 mt-auto mx-auto mb-4">
              <img
                src="https://media.loyaltify.io/static/logo/256.png"
                alt="Loyaltify Logo"
                className="w-8 h-auto"
              />
              <p className="text-sm">
                Powered by
                <a
                  href="https://loyaltify.io"
                  target="_blank"
                  className="text-indigo-500 hover:text-indigo-600"
                >
                  {" "}
                  Loyaltify
                </a>
              </p>
            </div>
          </div>
        </>
      ) : (
        <div>Loading...</div>
      )}
    </>
  );
}
